import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faArrowUp,
  faArrowDown,
  faGlobe,
  faDesktop,
  faCloud,
  faPlug,
  faCube,
  faUserShield,
  faLaptopCode,
  faNetworkWired,
  faBullseye
} from '@fortawesome/free-solid-svg-icons';
import ActionDetailModal from '../common/ActionDetailModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const RESULT_COLORS = {
  successful: '#28a745',
  detected: '#ffc107',
  blocked: '#dc3545',
  failed: '#6c757d',
  partial: '#17a2b8',
  unknown: '#6610f2'
};

const RESULT_BADGES = {
  successful: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  detected: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  failed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  partial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
};

const TARGET_TYPE_COLORS = {
  ip: '#0066cc',
  hostname: '#0080ff',
  cloudResource: '#4d94ff',
  apiEndpoint: '#66a3ff',
  container: '#1a75ff',
  identity: '#0052cc',
  application: '#3385ff',
  network: '#005ce6'
};

const TARGET_TYPE_ICONS = {
  ip: faGlobe,
  hostname: faDesktop,
  cloudResource: faCloud,
  apiEndpoint: faPlug,
  container: faCube,
  identity: faUserShield,
  application: faLaptopCode,
  network: faNetworkWired
};

const TargetDetailModal = ({ target, actions, allTargets, isOpen, onClose, projectId }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 350 });

  // Compute all actions involving this target (as target or source)
  const { incomingActions, outgoingActions, allRelatedActions, graphData } = useMemo(() => {
    if (!target || !actions || actions.length === 0) {
      return { incomingActions: [], outgoingActions: [], allRelatedActions: [], graphData: { nodes: [], links: [] } };
    }

    const targetId = String(target._id);
    const incoming = []; // target appears in action.targets
    const outgoing = []; // target appears in action.sources

    const getId = (ref) => {
      if (typeof ref === 'string') return ref;
      const id = ref?._id || ref?.id;
      return id ? String(id) : null;
    };
    const getLabel = (ref) => {
      if (typeof ref === 'string') {
        const found = allTargets?.find(t => String(t._id) === ref);
        return found ? found.value : ref.substring(0, 8);
      }
      return ref?.value || (ref?._id ? String(ref._id).substring(0, 8) : '?');
    };
    const getType = (ref) => {
      if (typeof ref === 'string') {
        const found = allTargets?.find(t => String(t._id) === ref);
        return found?.type || 'ip';
      }
      return ref?.type || 'ip';
    };

    actions.forEach(action => {
      const inTargets = (action.targets || []).some(t => getId(t) === targetId);
      const inSources = (action.sources || []).some(s => getId(s) === targetId);
      if (inTargets) incoming.push(action);
      if (inSources) outgoing.push(action);
    });

    const allRelated = [...new Map([...incoming, ...outgoing].map(a => [a._id, a])).values()];

    // Build graph data
    const nodeMap = new Map();
    // Center node
    nodeMap.set(targetId, {
      id: targetId,
      label: target.value,
      type: target.type || 'ip',
      isCenter: true
    });

    // Collect related targets from actions
    const linkMap = new Map(); // key: "sourceId->targetId" -> { actions, resultCounts }

    allRelated.forEach(action => {
      const actionSources = (action.sources || []);
      const actionTargets = (action.targets || []);

      // Add source nodes
      actionSources.forEach(s => {
        const sid = getId(s);
        if (sid && !nodeMap.has(sid)) {
          nodeMap.set(sid, { id: sid, label: getLabel(s), type: getType(s), isCenter: false });
        }
      });

      // Add target nodes
      actionTargets.forEach(t => {
        const tid = getId(t);
        if (tid && !nodeMap.has(tid)) {
          nodeMap.set(tid, { id: tid, label: getLabel(t), type: getType(t), isCenter: false });
        }
      });

      // Create edges: each source -> each target (for this action)
      const sources = actionSources.length > 0 ? actionSources.map(getId).filter(Boolean) : [null];
      const targets = actionTargets.length > 0 ? actionTargets.map(getId).filter(Boolean) : [null];

      sources.forEach(sid => {
        targets.forEach(tid => {
          if (!sid && !tid) return;
          // If action has only targets and no sources, create an edge from a virtual "action" to target
          const effectiveSource = sid || 'action-origin';
          const effectiveTarget = tid || 'action-dest';

          if (effectiveSource === 'action-origin' || effectiveTarget === 'action-dest') return;

          const key = `${effectiveSource}->${effectiveTarget}`;
          if (!linkMap.has(key)) {
            linkMap.set(key, { source: effectiveSource, target: effectiveTarget, actions: [], resultCounts: {} });
          }
          const link = linkMap.get(key);
          link.actions.push(action);
          const result = action.result || 'unknown';
          link.resultCounts[result] = (link.resultCounts[result] || 0) + 1;
        });
      });
    });

    // For incoming-only actions (no sources defined), show them as self-referencing indicators
    // Already handled: they just won't have edges, which is fine

    const nodes = Array.from(nodeMap.values());
    const links = Array.from(linkMap.values()).filter(l =>
      nodeMap.has(l.source) && nodeMap.has(l.target)
    );

    return {
      incomingActions: incoming,
      outgoingActions: outgoing,
      allRelatedActions: allRelated.sort((a, b) =>
        new Date(a.startTimestamp || a.createdAt) - new Date(b.startTimestamp || b.createdAt)
      ),
      graphData: { nodes, links }
    };
  }, [target, actions, allTargets]);

  // Responsive dimensions
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.max(400, width - 4), height: 350 });
      }
    };
    // Delay to allow modal to render
    const timer = setTimeout(handleResize, 50);
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // D3 Graph rendering
  useEffect(() => {
    if (!isOpen || !svgRef.current || graphData.nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    const { width, height } = dimensions;

    // Clear previous
    svg.selectAll('*').remove();

    // Background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', darkMode ? '#1f2937' : '#f9fafb')
      .attr('rx', 8);

    const g = svg.append('g');

    // Zoom
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => g.attr('transform', event.transform));
    svg.call(zoom);

    // Arrow markers per result color
    const defs = svg.append('defs');

    // Glow filter for center node
    const filter = defs.append('filter').attr('id', 'glow');
    filter.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'blur');
    filter.append('feComposite').attr('in', 'SourceGraphic').attr('in2', 'blur').attr('operator', 'over');

    // Arrow markers
    const markerColors = { ...RESULT_COLORS, default: darkMode ? '#9ca3af' : '#6b7280' };
    Object.entries(markerColors).forEach(([key, color]) => {
      defs.append('marker')
        .attr('id', `arrow-${key}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 28)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color);
    });

    // Prepare node/link data (clone to avoid D3 mutation issues)
    const nodes = graphData.nodes.map(n => ({ ...n }));
    const links = graphData.links.map(l => ({ ...l, source: l.source, target: l.target }));

    // Pin center node
    const centerNode = nodes.find(n => n.isCenter);
    if (centerNode) {
      centerNode.fx = width / 2;
      centerNode.fy = height / 2;
    }

    // Force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links)
        .id(d => d.id)
        .distance(120)
        .strength(0.6))
      .force('charge', d3.forceManyBody()
        .strength(d => d.isCenter ? -400 : -250))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.isCenter ? 40 : 30));

    simulationRef.current = simulation;

    // Draw links
    const link = g.append('g').attr('class', 'links').selectAll('line')
      .data(links)
      .enter().append('line')
      .attr('stroke', d => {
        // Dominant result color
        const counts = d.resultCounts || {};
        const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        return dominant ? (RESULT_COLORS[dominant[0]] || markerColors.default) : markerColors.default;
      })
      .attr('stroke-width', d => Math.min(1.5 + (d.actions?.length || 1) * 0.8, 5))
      .attr('opacity', 0.7)
      .attr('marker-end', d => {
        const counts = d.resultCounts || {};
        const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        const key = dominant ? dominant[0] : 'default';
        return `url(#arrow-${key})`;
      })
      .style('cursor', 'pointer');

    // Link hover/click
    link.on('click', function (event, d) {
      event.stopPropagation();
      if (d.actions && d.actions.length === 1) {
        setSelectedAction(d.actions[0]);
      } else if (d.actions && d.actions.length > 0) {
        setSelectedAction(d.actions[0]);
      }
    });

    // Edge labels (action count)
    const edgeLabels = g.append('g').attr('class', 'edge-labels').selectAll('text')
      .data(links.filter(l => l.actions && l.actions.length > 1))
      .enter().append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', -6)
      .style('font-size', '10px')
      .style('font-weight', '600')
      .style('fill', darkMode ? '#d1d5db' : '#4b5563')
      .style('pointer-events', 'none')
      .text(d => `${d.actions.length}x`);

    // Draw nodes
    const node = g.append('g').attr('class', 'nodes').selectAll('g')
      .data(nodes)
      .enter().append('g')
      .attr('cursor', 'pointer')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          if (!d.isCenter) {
            d.fx = null;
            d.fy = null;
          }
        }));

    // Node circles
    node.each(function (d) {
      const sel = d3.select(this);
      const radius = d.isCenter ? 24 : 16;
      const color = TARGET_TYPE_COLORS[d.type] || '#0066cc';

      if (d.isCenter) {
        // Glow ring
        sel.append('circle')
          .attr('r', radius + 5)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,3')
          .attr('opacity', 0.5)
          .attr('filter', 'url(#glow)');
      }

      sel.append('circle')
        .attr('r', radius)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', d.isCenter ? 3 : 2)
        .attr('opacity', d.isCenter ? 1.0 : 0.85);

      // Icon text inside
      sel.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .style('font-size', d.isCenter ? '12px' : '9px')
        .style('fill', '#fff')
        .style('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d.type === 'ip' ? 'IP' : d.type === 'hostname' ? 'H' : d.type.substring(0, 2).toUpperCase());
    });

    // Node labels
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => (d.isCenter ? 24 : 16) + 14)
      .style('font-size', d => d.isCenter ? '12px' : '10px')
      .style('font-weight', d => d.isCenter ? '700' : '500')
      .style('fill', darkMode ? '#e5e7eb' : '#1f2937')
      .style('pointer-events', 'none')
      .text(d => d.label.length > 20 ? d.label.substring(0, 18) + '...' : d.label);

    // Tooltip
    let tooltipTimer = null;
    const tooltip = d3.select('body').append('div')
      .attr('class', 'target-detail-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '10px 14px')
      .style('border-radius', '8px')
      .style('font-size', '12px')
      .style('pointer-events', 'auto')
      .style('max-width', '280px')
      .style('z-index', '99999')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)');

    tooltip
      .on('mouseenter', () => { if (tooltipTimer) { clearTimeout(tooltipTimer); tooltipTimer = null; } })
      .on('mouseleave', () => { tooltip.transition().duration(200).style('opacity', 0); });

    // Shared helper for tooltips
    const centerId = String(target._id);
    const tipGetId = (ref) => {
      if (typeof ref === 'string') return ref;
      const id = ref?._id || ref?.id;
      return id ? String(id) : null;
    };

    const buildActionList = (actionList, refId) => {
      let html = '';
      actionList.forEach(a => {
        const inSources = (a.sources || []).some(s => tipGetId(s) === refId);
        const inTargets = (a.targets || []).some(t => tipGetId(t) === refId);
        const isOutgoing = inSources && !inTargets;
        const borderColor = isOutgoing ? '#14b8a6' : '#f97316';
        const arrow = isOutgoing ? '\u2191' : '\u2193';
        const resultColor = RESULT_COLORS[a.result] || '#999';
        html += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;padding:2px 0;border-left:3px solid ${borderColor};padding-left:6px">`;
        html += `<span style="font-size:10px">${arrow}</span>`;
        html += `<span style="flex:1;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px">${a.title || 'Untitled'}</span>`;
        html += `<span style="font-size:10px;color:${resultColor};white-space:nowrap">${a.result || '?'}</span>`;
        html += `</div>`;
      });
      return html;
    };

    // Node tooltip - list actions involving this node
    node.on('mouseover', (event, d) => {
      if (tooltipTimer) { clearTimeout(tooltipTimer); tooltipTimer = null; }
      const nodeId = String(d.id);
      const nodeActions = allRelatedActions.filter(a => {
        const inTargets = (a.targets || []).some(t => tipGetId(t) === nodeId);
        const inSources = (a.sources || []).some(s => tipGetId(s) === nodeId);
        return inTargets || inSources;
      });

      let content = `<div style="font-weight:bold;margin-bottom:4px">${d.label}</div>`;
      content += `<div style="opacity:0.8;margin-bottom:4px">${d.type} \u2022 ${nodeActions.length} action${nodeActions.length !== 1 ? 's' : ''}</div>`;
      if (nodeActions.length > 0) {
        content += buildActionList(nodeActions.slice(0, 8), centerId);
        if (nodeActions.length > 8) {
          content += `<div style="opacity:0.5;font-size:10px;margin-top:2px">+${nodeActions.length - 8} more</div>`;
        }
      }
      if (d.isCenter) content += `<div style="margin-top:4px;color:#60a5fa;font-size:11px">Selected target</div>`;

      tooltip.html(content)
        .style('left', (event.pageX + 12) + 'px')
        .style('top', (event.pageY - 10) + 'px')
        .style('max-width', '340px')
        .transition().duration(150).style('opacity', 1);
    }).on('mouseout', () => {
      tooltipTimer = setTimeout(() => tooltip.transition().duration(200).style('opacity', 0), 400);
    });
    // Link tooltip - show action list with incoming/outgoing colors
    link.on('mouseover', (event, d) => {
      if (tooltipTimer) { clearTimeout(tooltipTimer); tooltipTimer = null; }
      const sourceNode = nodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
      const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));

      let content = `<div style="font-weight:bold;margin-bottom:6px">${sourceNode?.label || '?'} \u2192 ${targetNode?.label || '?'}</div>`;
      if (d.actions && d.actions.length > 0) {
        content += buildActionList(d.actions, centerId);
      }
      content += `<div style="margin-top:4px;opacity:0.6;font-size:10px">Click to view details</div>`;

      tooltip.html(content)
        .style('left', (event.pageX + 12) + 'px')
        .style('top', (event.pageY - 10) + 'px')
        .style('max-width', '320px')
        .transition().duration(150).style('opacity', 1);
    }).on('mouseout', () => {
      tooltipTimer = setTimeout(() => tooltip.transition().duration(200).style('opacity', 0), 400);
    });

    // Tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      edgeLabels
        .attr('x', d => (d.source.x + d.target.x) / 2)
        .attr('y', d => (d.source.y + d.target.y) / 2);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
      tooltip.remove();
    };
  }, [isOpen, graphData, dimensions, darkMode, allRelatedActions]);

  // Cleanup simulation on unmount
  useEffect(() => {
    return () => {
      if (simulationRef.current) simulationRef.current.stop();
    };
  }, []);

  const classifyAction = useCallback((action) => {
    if (!target) return 'incoming';
    const targetId = String(target._id);
    const getId = (ref) => {
      if (typeof ref === 'string') return ref;
      const id = ref?._id || ref?.id;
      return id ? String(id) : null;
    };
    const inSources = (action.sources || []).some(s => getId(s) === targetId);
    const inTargets = (action.targets || []).some(t => getId(t) === targetId);
    if (inSources && !inTargets) return 'outgoing';
    if (inTargets && !inSources) return 'incoming';
    return inSources ? 'outgoing' : 'incoming';
  }, [target]);

  const handleClose = () => {
    setSelectedAction(null);
    onClose();
  };

  if (!isOpen || !target) return null;

  const incomingCount = incomingActions.length;
  const outgoingCount = outgoingActions.length;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal container */}
        <div
          className={`rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b shrink-0 ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0`}
                style={{ backgroundColor: TARGET_TYPE_COLORS[target.type] || '#0066cc' }}>
                <FontAwesomeIcon
                  icon={TARGET_TYPE_ICONS[target.type] || faBullseye}
                  className="text-white text-sm"
                />
              </div>
              <div className="min-w-0">
                <h2 className={`text-lg font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {target.value}
                </h2>
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${
                    target.type === 'ip'
                      ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                      : 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
                  }`}>
                    {target.type}
                  </span>
                  {target.description && (
                    <span className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {target.description}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-full transition-colors shrink-0 ${
                darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            {/* Network Graph */}
            {graphData.nodes.length > 1 && (
              <div className="p-4 pb-2" ref={containerRef}>
                <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('targetDetail.networkGraph')}
                </h3>
                <svg
                  ref={svgRef}
                  width={dimensions.width}
                  height={dimensions.height}
                  className="rounded-lg"
                  style={{ display: 'block' }}
                />
                {/* Mini legend */}
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {Object.entries(RESULT_COLORS).filter(([k]) => {
                    const hasResult = allRelatedActions.some(a => a.result === k);
                    return hasResult;
                  }).map(([key, color]) => (
                    <div key={key} className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: color }} />
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{key}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Timeline */}
            <div className="p-4 pt-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('targetDetail.actionTimeline')}
                </h3>
                <div className="flex items-center gap-3 text-xs">
                  <span className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="w-1 h-4 bg-orange-500 rounded-full inline-block" />
                    {t('targetDetail.incoming')} ({incomingCount})
                  </span>
                  <span className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="w-1 h-4 bg-teal-500 rounded-full inline-block" />
                    {t('targetDetail.outgoing')} ({outgoingCount})
                  </span>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                    {t('targetDetail.totalActions')}: {allRelatedActions.length}
                  </span>
                </div>
              </div>

              {allRelatedActions.length === 0 ? (
                <div className={`text-center py-8 rounded-lg ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <FontAwesomeIcon icon={faBullseye} className={`text-4xl mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('targetDetail.noActions')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {allRelatedActions.map(action => {
                    const direction = classifyAction(action);
                    const isIncoming = direction === 'incoming';
                    return (
                      <div
                        key={action._id}
                        onClick={() => setSelectedAction(action)}
                        className={`border-l-4 rounded-r-lg p-3 cursor-pointer transition-colors ${
                          isIncoming
                            ? 'border-orange-500'
                            : 'border-teal-500'
                        } ${
                          darkMode
                            ? 'bg-gray-900 hover:bg-gray-700'
                            : 'bg-gray-50 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            <FontAwesomeIcon
                              icon={isIncoming ? faArrowDown : faArrowUp}
                              className={`mt-1 text-xs ${isIncoming ? 'text-orange-500' : 'text-teal-500'}`}
                              title={isIncoming ? t('targetDetail.attackedTarget') : t('targetDetail.attackSource')}
                            />
                            <div className="min-w-0 flex-1">
                              <div className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                {action.title}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                {action.mitreAttackTechnique && (
                                  <span className="text-purple-500 text-xs">
                                    {action.mitreAttackTechnique.id}
                                    {action.mitreAttackTechnique.name && ` - ${action.mitreAttackTechnique.name}`}
                                  </span>
                                )}
                                {action.operator && (
                                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {action.operator}
                                  </span>
                                )}
                                {(action.startTimestamp || action.createdAt) && (
                                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {new Date(action.startTimestamp || action.createdAt).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap shrink-0 ${
                            RESULT_BADGES[action.result] || RESULT_BADGES.unknown
                          }`}>
                            {action.result || 'unknown'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Nested ActionDetailModal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />
    </>
  );
};

export default TargetDetailModal;
